import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Skeleton, Text } from '@chakra-ui/react';

import { Tooltip } from '~/components/tooltip';
import { AnimatedPrice } from '~/features/accreditation/components/animated-price';
import { useDynamicPrice } from '~/features/accreditation/hooks/use-dynamic-price';
import { formatWholePrice } from '~/features/accreditation/utils/compute-dynamic-price';
import type { FormalDegreeKey } from '~/features/checkout/constants';
import type { Currency } from '~/features/internationalization/models/currency';

const AUTO_DISMISS_MS = 5000;

function DiscountTooltipWrapper({
  autoShow,
  percentOff,
  children,
  enabled = true,
}: {
  autoShow?: boolean;
  percentOff: number;
  children: React.ReactElement;
  enabled?: boolean;
}) {
  const [autoOpen, setAutoOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!autoShow) return;
    setAutoOpen(true);
    timerRef.current = setTimeout(() => setAutoOpen(false), AUTO_DISMISS_MS);
    return () => clearTimeout(timerRef.current);
  }, [autoShow]);

  const tooltipProps = !enabled ? { isOpen: false } : autoOpen ? { isOpen: true } : {};

  return (
    <Tooltip
      aria-label={`${percentOff}% off coupon applied`}
      placement='top'
      variant='light'
      {...tooltipProps}
      label={
        <Text fontSize='xs' px='4px' py='2px'>
          {percentOff}% off coupon applied
        </Text>
      }
      hasArrow
    >
      {children}
    </Tooltip>
  );
}

export function DynamicPriceTitle({
  formalDegreeKey,
  fallback,
}: {
  formalDegreeKey: FormalDegreeKey;
  fallback: string;
}) {
  const { total, maxTotal, formattedTotal, isDiscounted, currency, isLoading, isError, percentOff } =
    useDynamicPrice(formalDegreeKey);
  const [animationDone, setAnimationDone] = useState(false);
  const handleAnimationComplete = useCallback(() => setAnimationDone(true), []);

  const skeletonProps = {
    as: 'span' as const,
    display: 'inline-block',
    endColor: 'whiteAlpha.400',
    height: '1em',
    startColor: 'whiteAlpha.200',
    verticalAlign: 'middle',
  };

  if (isError) {
    return <>{fallback}</>;
  }

  if (isLoading || !formattedTotal) {
    return (
      <>
        Less than <Skeleton {...skeletonProps} width='6ch' /> <Skeleton {...skeletonProps} width='3ch' />*
      </>
    );
  }

  if (currency !== 'USD') {
    if (isDiscounted && percentOff) {
      return (
        <>
          Less than{' '}
          <DiscountTooltipWrapper percentOff={percentOff} autoShow>
            <Box as='span' borderBottom='1px dashed' borderColor='gold.300' cursor='help' pb='1px' tabIndex={0}>
              {formattedTotal}
            </Box>
          </DiscountTooltipWrapper>
          *
        </>
      );
    }

    return <>Less than {formattedTotal}*</>;
  }

  if (isDiscounted && maxTotal && total) {
    return (
      <>
        Less than{' '}
        <DiscountTooltipWrapper autoShow={animationDone} enabled={animationDone} percentOff={percentOff!}>
          <Box
            as='span'
            borderBottom={animationDone ? '1px dashed' : 'none'}
            borderColor='gold.300'
            cursor={animationDone ? 'help' : 'default'}
            pb='1px'
            tabIndex={0}
          >
            <AnimatedPrice
              formatValue={(n) => formatWholePrice(n, currency as Currency)}
              from={maxTotal}
              to={total}
              onComplete={handleAnimationComplete}
            />
          </Box>
        </DiscountTooltipWrapper>{' '}
        USD*
      </>
    );
  }

  return <>Less than {formattedTotal} USD*</>;
}

export function DynamicPriceDescription({
  formalDegreeKey,
  fallback,
}: {
  formalDegreeKey: FormalDegreeKey;
  fallback: string;
}) {
  const { formattedEnrollmentFee, isLoading, isError } = useDynamicPrice(formalDegreeKey);

  const skeletonProps = {
    as: 'span' as const,
    display: 'inline-block',
    endColor: 'whiteAlpha.400',
    height: '1em',
    startColor: 'whiteAlpha.200',
    verticalAlign: 'middle',
  };

  if (isError) {
    return <>{fallback}</>;
  }

  if (isLoading || !formattedEnrollmentFee) {
    return (
      <>
        Only <Skeleton {...skeletonProps} width='4ch' /> + Udacity subscription to start.
      </>
    );
  }

  return <>Only {formattedEnrollmentFee} + Udacity subscription to start.</>;
}
